"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@lib/auth/useAuth";
import type { StepFormProps } from "../../StepForm";
import StepForm from "../../StepForm";
import { Box } from "@ecco/ui/box";
import { IconButton } from "@ecco/ui/icon-button";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { activeSiteConfig } from "@config/site/site-config";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import {
    CANCEL_PENDING_QR_TEST_ID,
    PAYMENT_STEP_TEST_ID,
} from "@e2e/helpers/checkout/components/payment/constants";
import { LogTag, ServiceType, Logger } from "@lib/monitoring/logger";
import { useStore } from "@store/index";
import { useApi } from "@lib/helpers/useApi";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { fetchHeapPaymentMethods } from "@lib/utils/payment-methods/fetchHeapPaymentMethods";
import { PaymentIcons } from "@components/commerce/paymentIcons/PaymentIcons";
import { ensureActiveSession } from "@lib/auth/ensureActiveSession";
import { useCheckoutErrorState } from "@features/checkout/utils/useCheckoutErrorState";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import PaymentMessage from "../PaymentMessage";
import PaymentMessageWithLoading from "../PaymentMessageWithLoading";
import { TransparentScreenLock } from "../payment-screen-lock/TransparentScreenLock";
import { UI_STATUS, type UIStatus } from "../constants/uiStatus";
import {
    CheckoutStep,
    type THeapPaymentMethodsJson,
    type THeapPaymentMethodsResponse,
} from "@features/checkout/types";
import type {
    AdyenInstanceControls,
    CreateAdyenInstanceParams,
} from "../utils/adyen/createAdyenInstance";
import { createAdyenInstance } from "../utils/adyen/createAdyenInstance";
import type { AdyenRefusalResponse } from "../utils/adyen/parsePaymentResult";
import { useLocale } from "next-intl";
import {
    type CheckoutErrorType,
    type HeapPaymentErrorType,
    CHECKOUT_ERRORS,
    HEAP_PAYMENT_ERRORS,
} from "@lib/utils/checkout-utility/types";

import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";
import { useRouter, useSearchParams } from "next/navigation";
import {
    getPagePathByLocale,
    orderConfirmationPage,
    shoppingBagPage,
} from "@lib/constants/pagePaths";
import { useValidateBasket } from "../hooks/useValidateBasket";
import { useGetActiveCart } from "@lib/commerce/hooks/cartOperations";
import { HeapAmazonPayPaymentOption } from "./HeapAmazonPayPaymentOption";
import { getCartDiscount, getCartSubTotalPrice } from "@lib/utils/cart-utility/cartUtility";
import { getRoundedCartTotal } from "@lib/utils/moneyUtils";
import type { Cart } from "@graphql/generated/types";

export type HeapPaymentStepFormProps = StepFormProps & {
    orderCreationError: ReactNode | string;
    cartOrSessionChanged?: boolean;
    recaptchaAgreement?: ReactNode;
    cartId: string;
    cart: Cart;
    isSubscribed?: boolean;
    onStepError: (errorType: HeapPaymentErrorType | CheckoutErrorType, errValue?: string) => void;
};

const isAdyenRefusalResponse = (error: unknown): error is AdyenRefusalResponse =>
    typeof error === "object" && error !== null && "refusalReason" in error;

export function HeapPaymentStepForm({
    title,
    cartOrSessionChanged,
    recaptchaAgreement,
    orderCreationError,
    cartId,
    cart,
    isExpandedId,
    stepId,
    handleExpanded,
    isSubscribed,
    onStepError,
    ...stepFormProps
}: HeapPaymentStepFormProps) {
    const paymentRef = useRef<HTMLDivElement | null>(null);
    // A displayed Swish QR replaces the payment methods with no way back, so we offer a cancel.
    const [isPendingQr, setIsPendingQr] = useState(false);
    const paymentInstance = useRef<AdyenInstanceControls | null>(null);
    // On a fresh redirect return, onCancel can fire before mountAdyen sets UI_STATUS.IDLE,
    // while paymentRef is still display: none. Track the intent and scroll once IDLE commits.
    const pendingScrollToPaymentRef = useRef(false);
    const locale = useLocale();
    // Stable getter for Adyen callbacks (3DS, redirect return) that fire later and need the
    // latest cart at fire time, not the cart captured when the mount effect ran.
    const getCart = useStore((state) => state.getCart);
    const utmParams = useStore((state) => state.data.tracking.utm);
    const getOrCreateShopperConversionId = useStore(
        (state) => state.getOrCreateShopperConversionId
    );
    // Generate the id once on mount; subsequent renders reuse the value from the store.
    const shopperConversionId = useMemo(() => getOrCreateShopperConversionId(), []);
    // The store can be empty by the time a UPI QR is paid.
    const renderedCartRef = useRef(cart);
    useEffect(() => {
        renderedCartRef.current = cart;
    }, [cart]);
    const { totalPrice, taxedPrice, lineItems, shippingInfo } = cart;
    const totalGross = taxedPrice?.totalGross ?? totalPrice;
    const { push } = useRouter();
    const searchParams = useSearchParams();
    const redirectResult = searchParams.get("redirectResult");
    const redirectPaymentFlowId = searchParams.get("paymentFlowId");

    const roundedTotal = useMemo(
        () =>
            getRoundedCartTotal({
                totalGross,
                subtotalPrice: getCartSubTotalPrice(cart),
                items: lineItems ?? [],
                shippingInfo,
                cartDiscount: getCartDiscount(cart),
            })?.total ?? totalGross,
        [cart, totalGross, lineItems, shippingInfo]
    );

    const amount = roundedTotal?.centAmount;
    const currencyCode = roundedTotal?.currencyCode;

    const { validateBasket } = useValidateBasket();

    const { session } = useAuth();
    const getCurrentCart = useGetActiveCart();

    const [showPaymentScreenLock, setShowPaymentScreenLock] = useState(false);
    // Tracks the full lifecycle of an Adyen payment attempt, including the 3DS/QR/await
    // window where showPaymentScreenLock has been released so the user can interact with
    // the challenge. Used to keep the Amazon Pay button hidden for that whole window.
    const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);

    const [uiStatus, setUiStatus] = useState<UIStatus | null>(UI_STATUS.INITIALIZING);
    const { errors, setStepError, clearStepError } = useCheckoutErrorState();
    const errorMessage = errors[stepId];
    const { getMultiple, get: getMicrocopy } = useMicrocopy();

    const microcopies = useMemo(
        () =>
            getMultiple(CommonCMS.page, [
                Checkout.step3.spinnerTitle,
                Checkout.step3.spinnerSubtitle,
                Checkout.step3.dropinComponent,
                Checkout.step3.dropinError,
                Checkout.step3.dropinSessionError,
                Checkout.step3.orderCreation,
                Checkout.step3.paymentProcessingHeader,
                Checkout.step3.paymentProcessingMessage,
            ]),
        [getMultiple]
    );

    const showLoadingStatus = useMemo(() => {
        const loadingStatuses: UIStatus[] = [UI_STATUS.INITIALIZING, UI_STATUS.ORDER_PROCESSING];
        return loadingStatuses.includes(uiStatus);
    }, [uiStatus]);

    const loadingMessage = useMemo(() => {
        if (uiStatus === UI_STATUS.INITIALIZING) {
            return microcopies[Checkout.step3.spinnerSubtitle];
        }
        if (uiStatus === UI_STATUS.ORDER_PROCESSING) {
            return microcopies[Checkout.step3.orderCreation];
        }
        return null;
    }, [uiStatus, microcopies]);

    // Email lands in the cart once Contact Details is submitted. We include it in the
    // query key so HEAP refetches /paymentMethods after delivery — giving Adyen access
    // to the shopper's email (read server-side from the cart) for dynamic ordering.
    const shopperEmail = cart?.shippingAddress?.email;

    const {
        data: paymentMethodsResponse,
        isLoading: isLoadingPaymentMethods,
        isError: isPaymentMethodsError,
    } = useApi.useAsync<THeapPaymentMethodsResponse>(
        [
            QueryKeys.getPaymentMethods,
            "heap",
            shippingInfo?.shippingMethodName,
            cartId,
            shopperEmail,
            // Refetch the HEAP-signed Amazon payload whenever the payable amount changes
            // (giftwrap, tax, shipping, discounts, rounding) so its chargeAmount stays
            // correct. Keyed on the same canonical total charged via Adyen.
            amount,
        ],
        async () => {
            await ensureActiveSession(session);
            const result = await fetchHeapPaymentMethods(cartId, shopperConversionId);
            const paymentMethodsJson = JSON.parse(
                result.paymentMethodsJson
            ) as THeapPaymentMethodsJson[];
            return {
                ...result,
                parsedPaymentMethodsJson: paymentMethodsJson,
            };
        },
        {
            enabled: !!cartId && isExpandedId === stepId && !!session,
            staleTime: 0,
            meta: {
                serviceType: ServiceType.ADYEN,
                message: `HEAP PAYMENT: Issue loading payment methods for cartId: ${cartId}`,
                tag: LogTag.PAYMENT,
                payload: { cartId },
            },
        }
    );

    const adyenClientKey = paymentMethodsResponse?.adyenClientKey;
    const adyenProvider = paymentMethodsResponse?.parsedPaymentMethodsJson?.find(
        (config) => config.PaymentProviderType === "Adyen"
    );
    const paymentMethods =
        adyenProvider?.PaymentProviderType === "Adyen" ? adyenProvider.paymentMethods : undefined;

    const amazonPayProvider = paymentMethodsResponse?.parsedPaymentMethodsJson?.find(
        (config) => config.PaymentProviderType === "AmazonPay"
    );
    const amazonPayMethod =
        amazonPayProvider?.PaymentProviderType === "AmazonPay"
            ? amazonPayProvider.paymentMethods[0]
            : undefined;

    const accordionHeadingContent = useMemo(() => {
        if (isExpandedId < 2) {
            return <PaymentIcons opacity={0.5} />;
        }
    }, [isExpandedId]);

    // Runs if cart validation fails during payment.
    const handleCartValidationFailure = () => {
        // Redirect to shopping bag on failure
        push(getPagePathByLocale(shoppingBagPage, locale));
    };

    const onPaymentError = (
        error: HeapPaymentErrorType | AdyenRefusalResponse | CheckoutErrorType,
        value?: string
    ) => {
        // Reset all loading states
        setShowPaymentScreenLock(false);
        setIsPaymentInProgress(false);
        setUiStatus(UI_STATUS.IDLE);
        // Only handle other payment errors if we're currently on the payment step
        if (isExpandedId !== CheckoutStep.PAYMENT) {
            return;
        }

        if (isAdyenRefusalResponse(error)) {
            const refusalReasonKey = Checkout.step3.dropinRefusalReasonCodes(
                error.refusalReasonCode || "default"
            );
            const refusalReasonMessage = getMicrocopy(CommonCMS.page, refusalReasonKey);
            setStepError(
                CheckoutStep.PAYMENT,
                refusalReasonMessage ?? microcopies[Checkout.step3.dropinError]
            );
            return;
        }

        switch (error) {
            case HEAP_PAYMENT_ERRORS.AddressObjectMissing:
            case HEAP_PAYMENT_ERRORS.PostCodeMissing:
            case HEAP_PAYMENT_ERRORS.StreetNameMissing:
            case HEAP_PAYMENT_ERRORS.AddressInvalid:
            case HEAP_PAYMENT_ERRORS.NameAndLastNameOrCompanyNameMissing:
            case HEAP_PAYMENT_ERRORS.Validation:
            case CHECKOUT_ERRORS.CCStoreAddressInvalid:
            case CHECKOUT_ERRORS.DeliveryMethodInvalid:
            case CHECKOUT_ERRORS.ExpressUnavailableForProducts:
                onStepError(error);
                handleExpanded(CheckoutStep.DELIVERY);
                break;

            case HEAP_PAYMENT_ERRORS.PAYMENT_FAILED:
                setStepError(CheckoutStep.PAYMENT, microcopies[Checkout.step3.dropinError]);
                break;

            case HEAP_PAYMENT_ERRORS.CartValidationFailure:
            case CHECKOUT_ERRORS.ItemsOutOfStock:
            case CHECKOUT_ERRORS.CartInvalid:
                handleCartValidationFailure();
                break;

            case CHECKOUT_ERRORS.MissingEmail:
            case CHECKOUT_ERRORS.EmailInvalid:
            case CHECKOUT_ERRORS.MissingPhone:
            case CHECKOUT_ERRORS.PhoneInvalid:
                onStepError(error, value);
                handleExpanded(CheckoutStep.CONTACT_DETAILS);
                break;

            default: {
                Logger.error(
                    ServiceType.ADYEN,
                    `Unhandled HEAP_PAYMENT_ERRORS type in HeapPaymentStepForm.tsx onPaymentError: ${error}`,
                    {
                        serviceType: ServiceType.ADYEN,
                        tag: LogTag.PAYMENT,
                        error,
                        cartId,
                    }
                );
                // If adding a new HEAP_PAYMENT_ERRORS type this will ensure we don't forget to handle it here
                const _exhaustiveCheck: never = error;
                return _exhaustiveCheck;
            }
        }
    };

    const onSuccess = (orderNumber: string, orderDraftId?: string | null) => {
        setUiStatus(UI_STATUS.ORDER_PROCESSING);
        setShowPaymentScreenLock(false);
        setIsPaymentInProgress(false);
        const id = orderDraftId || orderNumber;
        AnalyticsClient.checkoutProgress(CheckoutProgressSubtypes.purchase);
        push(`${getPagePathByLocale(orderConfirmationPage, locale)}/v2/${id}`);
    };

    const onPaymentStart = () => {
        clearStepError(CheckoutStep.PAYMENT);
        setShowPaymentScreenLock(true);
        setIsPaymentInProgress(true);
    };

    const releaseLockscreen = () => {
        // Unblock the UI to allow users to interact with 3DS.
        setShowPaymentScreenLock(false);
    };

    const onPaymentCancelled = () => {
        setShowPaymentScreenLock(false);
        setIsPaymentInProgress(false);
        setUiStatus(UI_STATUS.IDLE);
        // The shopper's likely scrolled away during the redirect round trip, so bring the
        // payment step back into view instead of leaving them stranded wherever the page landed.
        // Deferred to the effect below since the payment element may still be display: none here.
        pendingScrollToPaymentRef.current = true;
    };

    // Runs the scroll requested by onPaymentCancelled once the payment element is actually visible.
    useEffect(() => {
        if (!pendingScrollToPaymentRef.current || uiStatus !== UI_STATUS.IDLE) return;
        pendingScrollToPaymentRef.current = false;
        paymentRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, [uiStatus]);

    // Keep latest validateBasket on the Adyen instance without re-running the mount effect every render
    // (useValidateBasket returns a new function reference each render).
    useEffect(() => {
        paymentInstance.current?.updateValidateBasket(validateBasket);
    }, [validateBasket]);

    // Mount adyen widget when we have the required data.
    // Deps intentionally exclude validateBasket (new ref each render) and callbacks — validateBasket is synced via updateValidateBasket.
    useEffect(() => {
        if (isExpandedId !== CheckoutStep.PAYMENT) return;

        // HTML element not ready yet
        if (!paymentRef.current) return;

        if (paymentInstance.current) {
            return;
        }

        if (!paymentMethodsResponse && isLoadingPaymentMethods) {
            return;
        }

        // There are no payment methods or we have to API key
        if (
            !Array.isArray(paymentMethods) ||
            paymentMethods.length === 0 ||
            isPaymentMethodsError ||
            !adyenClientKey
        ) {
            setUiStatus(UI_STATUS.BLOCKED);
            setStepError(CheckoutStep.PAYMENT, microcopies[Checkout.step3.dropinSessionError]);
            return;
        }

        let cancelled = false;

        const mountAdyen = async () => {
            const targetElement = paymentRef.current;
            if (!targetElement) return;

            const createAdyenArgs: CreateAdyenInstanceParams = {
                paymentMethods,
                amount: {
                    value: amount,
                    currency: currencyCode,
                },
                cartId,
                locale,
                redirectResult,
                redirectPaymentFlowId,
                targetElement,
                translations: microcopies[Checkout.step3.dropinComponent],
                utm: utmParams,
                isSubscribed,
                shopperConversionId,
                validateBasket,
                getCurrentCart,
                onError: onPaymentError,
                onSuccess: onSuccess,
                onPaymentStart: onPaymentStart,
                onPendingQrChange: setIsPendingQr,
                releaseLockscreen,
                onCancel: onPaymentCancelled,
                getCart: () => getCart() ?? renderedCartRef.current,
                logParams: {},
                apiKey: adyenClientKey,
            };
            const instance = await createAdyenInstance(createAdyenArgs);
            if (cancelled) return;
            if (paymentRef.current !== targetElement) return;

            paymentInstance.current = instance;
            instance.mountDropin();
            setUiStatus(UI_STATUS.IDLE);
        };
        mountAdyen();

        return () => {
            cancelled = true;
        };
    }, [
        adyenClientKey,
        amount,
        cartId,
        currencyCode,
        isExpandedId,
        isLoadingPaymentMethods,
        isPaymentMethodsError,
        paymentMethods,
        paymentMethodsResponse,
        redirectResult,
        redirectPaymentFlowId,
        locale,
        utmParams,
    ]);

    // Drop instance ref when leaving payment (accordion unmounts this tree; keeps remounts clean)
    useEffect(() => {
        if (isExpandedId !== CheckoutStep.PAYMENT) {
            paymentInstance.current = null;
        }
    }, [isExpandedId]);

    // Update paymentInstance parameters when they change.
    useEffect(() => {
        if (!paymentInstance.current) return;
        paymentInstance.current.updateCartId(cartId);
        paymentInstance.current.updateAmount({
            value: amount,
            currency: currencyCode,
        });
    }, [cartId, amount, currencyCode]);

    // When the browser restores this page from bfcache (e.g. user hits back from Klarna),
    // the Adyen dropin is frozen AND our loading/lock React state is still in its pre-redirect
    // values (bfcache preserves state). Refresh the dropin via updateAmount() on the existing
    // instance and clear the loading overlays so the user sees the interactive payment UI again.
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            const instance = paymentInstance.current;
            if (
                event.persisted &&
                sessionStorage.getItem("adyen_payment_method_type") &&
                instance
            ) {
                setShowPaymentScreenLock(false);
                setIsPaymentInProgress(false);
                setUiStatus(UI_STATUS.IDLE);
                instance.updateAmount({
                    value: amount,
                    currency: currencyCode,
                });
            }
        };
        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    }, [amount, currencyCode]);

    return (
        <StepForm
            stepId={stepId}
            title={title}
            errorMessage={errorMessage}
            isExpandedId={isExpandedId}
            accordionHeadingContent={accordionHeadingContent}
            handleExpanded={handleExpanded}
            {...stepFormProps}
        >
            {showPaymentScreenLock && (
                <TransparentScreenLock
                    header={microcopies[Checkout.step3.paymentProcessingHeader]}
                    message={microcopies[Checkout.step3.paymentProcessingMessage]}
                />
            )}

            {isPendingQr && (
                <Box display="flex" justifyContent="flex-end">
                    <IconButton
                        variant="ghost"
                        aria-label={getMicrocopy(CommonCMS.shared, CommonCMS.button.cancel) ?? ""}
                        icon={<ECCOIcon aria-hidden name="close" size="sm" />}
                        onClick={() => paymentInstance.current?.cancelPendingQr()}
                        data-testid={CANCEL_PENDING_QR_TEST_ID}
                    />
                </Box>
            )}

            <Box
                ref={paymentRef}
                display={showLoadingStatus ? "none" : "block"}
                data-testid={PAYMENT_STEP_TEST_ID}
            />

            {!showLoadingStatus && !isPaymentInProgress && amazonPayMethod && (
                <HeapAmazonPayPaymentOption
                    method={amazonPayMethod}
                    cart={cart}
                    cartId={cartId}
                    locale={locale}
                    isSubscribed={isSubscribed}
                    utm={utmParams}
                />
            )}

            {showLoadingStatus && (
                <>
                    {uiStatus === UI_STATUS.INITIALIZING && (
                        <PaymentMessage
                            message={microcopies[Checkout.step3.spinnerTitle]}
                            mb={3}
                            mt={4}
                        />
                    )}
                    <PaymentMessageWithLoading
                        message={loadingMessage}
                        color="gray.800"
                        mb={5}
                        mt={4}
                    />
                </>
            )}

            {recaptchaAgreement && activeSiteConfig.reCaptchaIntegration.checkout && (
                <Box pt={6} w="100%" maxW={75}>
                    {recaptchaAgreement}
                </Box>
            )}
        </StepForm>
    );
}

export default HeapPaymentStepForm;
